import { escaparHtml } from './painel-administrativo.utils';

/**
 * Diálogos do painel administrativo no molde dos modais do Meu RH (uib-modal): cabeçalho com ícone,
 * corpo com ícone grande à esquerda, pergunta em destaque e botões à direita no rodapé.
 * Substitui window.prompt/alert, que destoam do sistema.
 */
type Tom = 'perigo' | 'alerta' | 'sucesso' | 'info';

const ICONE: { [tom: string]: string } = {
    perigo: 'fa-times-circle',
    alerta: 'fa-exclamation-triangle',
    sucesso: 'fa-check-circle',
    info: 'fa-info-circle',
};

export interface OpcoesConfirmacao {
    titulo: string;
    pergunta: string;
    /** Texto ou HTML já escapado de quem chama. */
    explicacaoHtml?: string;
    tom?: Tom;
    rotuloConfirmar: string;
    iconeConfirmar?: string;
    /** Mostra um campo de texto opcional (ex.: motivo) e devolve o valor digitado. */
    campoTexto?: { rotulo: string; placeholder?: string };
}

export interface RespostaConfirmacao {
    confirmado: boolean;
    texto: string;
}

class PainelAdministrativoDialogo {

    confirmar(opcoes: OpcoesConfirmacao): Promise<RespostaConfirmacao> {
        const tom = opcoes.tom || 'alerta';
        const botaoConfirmar = tom === 'perigo' || tom === 'alerta' ? 'pa-btn--perigo' : '';

        return this.abrir(`
            ${this.cabecalho(opcoes.titulo, tom)}
            <div class="pa-modal-corpo">
                <i class="fas ${ICONE[tom]} pa-modal-icone pa-modal-icone--${tom}"></i>
                <div class="pa-modal-texto">
                    <h4>${escaparHtml(opcoes.pergunta)}</h4>
                    ${opcoes.explicacaoHtml || ''}
                    ${opcoes.campoTexto ? `
                        <label class="pa-modal-campo">${escaparHtml(opcoes.campoTexto.rotulo)}
                            <textarea data-dialogo-texto placeholder="${escaparHtml(opcoes.campoTexto.placeholder || '')}" maxlength="500"></textarea>
                        </label>` : ''}
                </div>
            </div>
            <div class="pa-modal-rodape">
                <button class="pa-btn pa-btn--secundario" data-dialogo="cancelar">Cancelar</button>
                <button class="pa-btn ${botaoConfirmar}" data-dialogo="confirmar">
                    ${opcoes.iconeConfirmar ? `<i class="fas ${escaparHtml(opcoes.iconeConfirmar)}"></i>` : ''}${escaparHtml(opcoes.rotuloConfirmar)}
                </button>
            </div>
        `, opcoes.campoTexto ? '[data-dialogo-texto]' : '[data-dialogo="confirmar"]');
    }

    /**
     * Formulário curto em modal. `coletar` lê os campos de dentro do corpo e devolve o valor, ou
     * lança Error com a mensagem de validação (mostrada no próprio modal, sem fechar).
     */
    formulario<T>(opcoes: {
        titulo: string;
        icone: string;
        corpoHtml: string;
        rotuloConfirmar: string;
        coletar: (corpo: HTMLElement) => T;
    }): Promise<T | null> {
        return new Promise(resolve => {
            const raiz = (document.querySelector('.pa') as HTMLElement) || document.body;
            const elemento = document.createElement('div');
            elemento.className = 'pa-modal-fundo';
            elemento.innerHTML = `
                <div class="pa-modal" role="dialog" aria-modal="true">
                    <div class="pa-modal-cabecalho">
                        <h3><i class="fas ${escaparHtml(opcoes.icone)}"></i> ${escaparHtml(opcoes.titulo)}</h3>
                        <button class="pa-modal-fechar" data-dialogo="cancelar" aria-label="Fechar">×</button>
                    </div>
                    <div class="pa-modal-formulario" data-dialogo-corpo>
                        ${opcoes.corpoHtml}
                        <div class="pa-modal-erro" data-dialogo-erro hidden></div>
                    </div>
                    <div class="pa-modal-rodape">
                        <button class="pa-btn pa-btn--secundario" data-dialogo="cancelar">Cancelar</button>
                        <button class="pa-btn" data-dialogo="confirmar"><i class="fas fa-save"></i>${escaparHtml(opcoes.rotuloConfirmar)}</button>
                    </div>
                </div>`;
            raiz.appendChild(elemento);

            const corpo = elemento.querySelector('[data-dialogo-corpo]') as HTMLElement;
            const erro = elemento.querySelector('[data-dialogo-erro]') as HTMLElement;

            const fechar = (valor: T | null) => {
                document.removeEventListener('keydown', teclado, true);
                elemento.remove();
                resolve(valor);
            };
            const confirmar = () => {
                try {
                    fechar(opcoes.coletar(corpo));
                } catch (e) {
                    erro.textContent = e.message;
                    erro.hidden = false;
                }
            };
            const teclado = (evento: KeyboardEvent) => {
                if (evento.key === 'Escape') {
                    evento.stopPropagation();
                    fechar(null);
                } else if (evento.key === 'Enter' && (evento.target as HTMLElement).tagName === 'INPUT') {
                    evento.preventDefault();
                    confirmar();
                }
            };
            document.addEventListener('keydown', teclado, true);

            elemento.addEventListener('click', (evento) => {
                const alvo = evento.target as HTMLElement;
                if (alvo === elemento) {
                    fechar(null);
                    return;
                }
                const botao = alvo.closest('[data-dialogo]') as HTMLElement | null;
                if (botao) {
                    botao.getAttribute('data-dialogo') === 'confirmar' ? confirmar() : fechar(null);
                }
            });

            const primeiro = corpo.querySelector('input, select, textarea') as HTMLElement | null;
            if (primeiro) {
                primeiro.focus();
            }
        });
    }

    avisar(titulo: string, mensagem: string, tom: Tom = 'perigo'): Promise<RespostaConfirmacao> {
        return this.abrir(`
            ${this.cabecalho(titulo, tom)}
            <div class="pa-modal-corpo">
                <i class="fas ${ICONE[tom]} pa-modal-icone pa-modal-icone--${tom}"></i>
                <div class="pa-modal-texto"><p>${escaparHtml(mensagem)}</p></div>
            </div>
            <div class="pa-modal-rodape">
                <button class="pa-btn" data-dialogo="confirmar">Entendi</button>
            </div>
        `, '[data-dialogo="confirmar"]');
    }

    private cabecalho(titulo: string, tom: Tom): string {
        return `
            <div class="pa-modal-cabecalho">
                <h3><i class="fas ${ICONE[tom]}"></i> ${escaparHtml(titulo)}</h3>
                <button class="pa-modal-fechar" data-dialogo="cancelar" aria-label="Fechar">×</button>
            </div>
        `;
    }

    private abrir(conteudo: string, focar: string): Promise<RespostaConfirmacao> {
        return new Promise(resolve => {
            const raiz = (document.querySelector('.pa') as HTMLElement) || document.body;
            const elemento = document.createElement('div');
            elemento.className = 'pa-modal-fundo';
            elemento.innerHTML = `<div class="pa-modal" role="dialog" aria-modal="true">${conteudo}</div>`;
            raiz.appendChild(elemento);

            const fechar = (confirmado: boolean) => {
                const campo = elemento.querySelector('[data-dialogo-texto]') as HTMLTextAreaElement | null;
                document.removeEventListener('keydown', teclado, true);
                elemento.remove();
                resolve({ confirmado, texto: campo ? campo.value.trim() : '' });
            };

            const teclado = (evento: KeyboardEvent) => {
                if (evento.key === 'Escape') {
                    evento.stopPropagation();
                    fechar(false);
                }
            };
            document.addEventListener('keydown', teclado, true);

            elemento.addEventListener('click', (evento) => {
                const alvo = evento.target as HTMLElement;
                if (alvo === elemento) {
                    fechar(false);
                    return;
                }
                const botao = alvo.closest('[data-dialogo]') as HTMLElement | null;
                if (botao) {
                    fechar(botao.getAttribute('data-dialogo') === 'confirmar');
                }
            });

            const foco = elemento.querySelector(focar) as HTMLElement | null;
            if (foco) {
                foco.focus();
            }
        });
    }
}

export const dialogo = new PainelAdministrativoDialogo();
